import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { MdOutlineAddAPhoto } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { Avatar } from '../../../components/Avatar'
import { Breadcrumb } from '../../../components/Breadcumb'
import { Button } from '../../../components/Button'
import { FloatingPromotersStayingTimeCounter } from '../../../components/FloatingPromotersStayingTimeCounter'
import { Input } from '../../../components/Input'
import { useDialogBox } from '../../../hooks/useDialogBox'
import { Role, roleMap } from '../../../interfaces/rolesEnum'
import { getUserProfile, updateProfile } from '../../../services/userServices'
import { compressImage } from '../../../utils/compressImage'
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber'

const profileFormSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  phone: z.string().transform((value) => value.replace(/\D/g, '')),
  currentPassword: z.string().optional(),
  newPassword: z.string().optional(),
  newPasswordConfirmation: z.string().optional(),
}).refine(values => !values.currentPassword || values.currentPassword.length >= 6, {
  message: 'A senha atual tem pelo menos 6 caracteres',
  path: ['currentPassword'],
}).refine(values => !values.newPassword || values.newPassword.length >= 6, {
  message: 'A nova senha deve ter pelo menos 6 caracteres',
  path: ['newPassword'],
}).refine(values => !values.newPassword || values.newPassword === values.newPasswordConfirmation, {
  message: 'As senhas estão diferentes',
  path: ['newPasswordConfirmation'],
}).refine(values => !values.newPassword || values.currentPassword, {
  message: 'É preciso fornecer a senha atual para alterar a senha',
  path: ['currentPassword'],
})

type ProfileFormData = z.infer<typeof profileFormSchema>

export function Profile() {
  const [avatarFile, setAvatarFile] = useState<File | undefined>()
  const [avatarPreview, setAvatarPreview] = useState<string | null>()

  const { showDialog } = useDialogBox()

  const { data: profile, refetch } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const userProfile = await getUserProfile()
      setAvatarPreview(userProfile.avatar_url)
      return userProfile
    },
  })

  const { register, handleSubmit, reset, watch, control, setValue, formState: { errors, isSubmitting } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: profile?.name,
      email: profile?.email,
      phone: profile?.phone,
    },
  })

  async function handleSubmitUpdateUser(data: ProfileFormData) {
    if (!profile) return
    const userProfile = {
      id: profile.id,
      avatar: avatarFile || undefined,
      email: profile.email !== data.email
        ? data.email
        : undefined,
      name: profile.name !== data.name
        ? data.name
        : undefined,
      phone: profile.phone !== data.phone
        ? data.phone
        : undefined,
      newPassword: data.newPassword || undefined,
      currentPassword: data.currentPassword || undefined,
    }

    const result = await updateProfile(userProfile)

    if (result) {
      if (result.success) {
        showDialog({
          title: 'Sucesso',
          description: result.message,
          type: 'success',
        })
        refetch()
        reset(result.user)
        setValue('currentPassword', '')
        setValue('newPassword', '')
        setValue('newPasswordConfirmation', '')
      } else {
        showDialog({
          title: 'Erro',
          description: result.message,
          type: 'error',
        })
      }
    }
  }

  const name = watch('name')

  useEffect(() => {
    if (profile) {
      reset(profile)
    }
  }, [profile, reset])

  async function handleChangeAvatar(event: ChangeEvent<HTMLInputElement>) {
    if (!profile) return
    const files = event.target.files
    if (files && files.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filesList = Array.from(files).map(async (file: any) => {
        if (!file.type.includes('image/')) {
          return null
        }
        const fileExtension = file.name.split('.').pop()
        const renamedFile = new File([file], `${profile.id}.${fileExtension}`, {
          type: file.type,
        })
        const compressedFile = await compressImage(renamedFile, 200)
        return compressedFile
      })

      const compressedFiles = await Promise.all(filesList)
      const [file] = compressedFiles.filter(file => file !== null)

      const preview = URL.createObjectURL(file)

      setAvatarFile(file)
      setAvatarPreview(preview)
    }
  }

  return (
    <div className="min-h-screen">
      <FloatingPromotersStayingTimeCounter />
      <div className="flex flex-col mx-auto max-w-screen-lg pb-16">
        <div className="px-2 lg:px-0 my-4">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Meu Perfil', path: '/profile' },
            ]}
          />
        </div>
        <header className="flex flex-col sm:flex-row justify-between items-center m-4 lg:mx-0">
          <h1
            className="mb-4 sm:mb-0 font-bold text-2xl"
          >
            Meu perfil
          </h1>
        </header>
        <section className="px-2 lg:px-0">
          <header>
            <div className="flex items-end relative w-fit mb-6">
              <Avatar className="w-20 h-20 border-2 border-indigo-400" imageUrl={avatarPreview || ''} />
              <label
                html-for="uploadFile1"
                className="flex items-center text-lg hover:text-gray-700 absolute right-[-1.25rem] bottom-[-0.5rem] p-2 outline-none rounded-md w-fit cursor-pointer"
                title="Mudar avatar"
              >
                <MdOutlineAddAPhoto />
                <input type="file" className="hidden" onChange={handleChangeAvatar} />
              </label>
            </div>
            <div className="my-6">
              <h1 className="font-bold text-xl">
                {name}
                {profile && ' - ' + roleMap[profile.role as Exclude<keyof typeof Role, 'ROOT'>]}
                {profile && profile.subrole && ' e ' + roleMap[profile.subrole as Exclude<keyof typeof Role, 'ROOT'>]}
              </h1>
            </div>
            <form
              onSubmit={handleSubmit(handleSubmitUpdateUser)}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <div>
                <strong className="block mb-2">Nome</strong>
                <Input
                  required
                  error={errors.name}
                  {...register('name')}
                />
              </div>
              <div>
                <strong className="block mb-2">Email</strong>
                <Input
                  required
                  error={errors.email}
                  {...register('email')}
                />
              </div>
              <div>
                <strong className="block mb-2">Telefone (WhatsApp)</strong>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      required
                      placeholder="(00) 0 0000-0000"
                      error={fieldState.error}
                      value={formatPhoneNumber(field.value || '')}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(formatPhoneNumber(e.target.value))}
                    />
                  )}
                />
              </div>
              <div>
                <strong className="block mb-2">Senha atual</strong>
                <Input
                  required
                  placeholder="Digite sua senha atual"
                  autoComplete="new-password"
                  type="password"
                  error={errors.currentPassword}
                  {...register('currentPassword')}
                />
              </div>
              <div>
                <strong className="block mb-2">Nova senha</strong>
                <Input
                  required
                  placeholder="Nova senha"
                  type="password"
                  error={errors.newPassword}
                  {...register('newPassword')}
                />
              </div>
              <div>
                <strong className="block mb-2">Confirme a nova senha</strong>
                <Input
                  required
                  placeholder="Confirme a nova senha"
                  type="password"
                  error={errors.newPasswordConfirmation}
                  {...register('newPasswordConfirmation')}
                />
              </div>
              <div className="flex justify-end col-span-1 md:col-span-2 lg:col-span-3 gap-4">
                <Link to="/dashboard" className="md:w-fit w-full">
                  <Button
                    type="button"
                    variant="outline"
                    isLoading={isSubmitting}
                  >
                    Calcelar
                  </Button>
                </Link>
                <Button
                  type="submit"
                  className="md:w-fit w-full"
                  isLoading={isSubmitting}
                >
                  Salvar
                </Button>
              </div>
            </form>
          </header>
        </section>
      </div>
    </div>
  )
}
